import { useScrollLock, useSheetDrag } from "./ui";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { derivedLandmarkDate, planDeadline, planFrom, planNumbers, planStartAt, suggestLandmark, type ClubBook, type PlanView } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { CalendarIcon, CloseIcon, PlusIcon } from "./Icons";
import { reading } from "@pagemates/shared/copy";

const txt = reading.planSheet;

const dateShort = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
const dayInput = (iso: string | null) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");

interface Row { id?: string; name: string; page: number | ""; date: string | null; key: number }

/**
 * The organizer plans a stretch: named landmarks by page, dates that follow
 * the club's pace unless set by hand, and what the meeting covers. Suggested
 * pages are starting points to overtype with the nearest chapter break.
 */
export function PlanSheet({ clubId, book, plan, meetingAt, usualPace, onClose }:
  { clubId: string; book: ClubBook; plan: PlanView; meetingAt: string | null; usualPace: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [pages, setPages] = useState<number | "">(plan.pages ?? "");
  // Between stretches the sheet plans the next one, from where the last ended.
  const from = planFrom(plan);
  const [rows, setRows] = useState<Row[]>(() => (plan.ended ? plan.landmarks.filter((l) => l.beyond) : plan.landmarks)
    .map((l, i) => ({ id: l.id, name: l.name, page: l.page, date: l.dateEdited ? dayInput(l.date) : null, key: i })));
  const [to, setTo] = useState<number | "whole">(plan.ended || plan.to === null ? "whole" : plan.to);
  const [custom, setCustom] = useState<number | "">("");
  // "another page" can be chosen before its page is typed; Save waits for a valid page.
  const [other, setOther] = useState(false);
  const customRef = useRef<HTMLInputElement>(null);
  const [editingDate, setEditingDate] = useState<number | null>(null);
  const nextKey = useRef(rows.length);
  const focusKey = useRef<number | null>(null);
  const dialog = useRef<HTMLDivElement>(null);

  const save = useMutation({
    mutationFn: () => api.savePlan(clubId, book.id, {
      landmarks: rows.filter((r) => r.page !== "").map((r) => ({ id: r.id, name: r.name, page: r.page as number, date: r.date || null })),
      to: to === "whole" ? null : to,
      ...(pages !== plan.pages ? { pages: pages === "" ? null : pages } : {}),
    }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["books", clubId] }); await qc.invalidateQueries({ queryKey: ["discussion", clubId, book.id] }); onClose(); },
  });

  useScrollLock();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  useEffect(() => {
    if (focusKey.current === null) return;
    const el = dialog.current?.querySelector<HTMLInputElement>(`#lm-page-${focusKey.current}`);
    el?.focus(); el?.select(); focusKey.current = null;
  }, [rows.length]);

  const total = pages === "" ? null : pages;
  const end = to === "whole" ? total : to;
  const startAt = planStartAt(plan, book.startedAt);
  const deadline = planDeadline(meetingAt);
  // paceToday is the whole book, for the subtitle
  const { pace, paceToday, reachAtUsual } = planNumbers({ from, total, end, startAt, deadline, usualPace });
  const derivedDate = (page: number) => derivedLandmarkDate(page, { from, end, startAt, deadline });
  const suggest = () => suggestLandmark(from, total, rows.map((r) => r.page).filter((p): p is number => p !== ""));
  function addRow() {
    const s = suggest();
    const key = nextKey.current++;
    focusKey.current = key;
    setRows((rs) => [...rs, { name: "", page: s?.page ?? "", date: null, key }]);
  }
  const sorted = [...rows].sort((a, b) => (a.page === "" ? 1 : b.page === "" ? -1 : a.page - b.page));
  const hint = suggest();
  const closest = reachAtUsual !== null && sorted.length ? sorted.reduce<Row | null>((b, r) => (r.page === "" ? b : !b || Math.abs(r.page - reachAtUsual) < Math.abs((b.page as number) - reachAtUsual) ? r : b), null) : null;
  const valid = total !== null && rows.every((r) => r.page !== "" && r.page > from && r.page <= total) && (other ? custom !== "" && custom > from && custom <= total : (to === "whole" || (to > from && to <= total)));

  useSheetDrag(dialog, onClose);
  // Rendered at the top of the page so it covers everything, the reading track included.
  return createPortal(
    <>
      <div className="modal-dim" onClick={onClose} />
      <div className="modal plan-sheet" role="dialog" aria-label={txt.planTheRead} ref={dialog}>
        <div className="grab" />
        <div className="head"><b>{from > 0 ? txt.planThisStretchFrom(from) : txt.planTheRead}</b><button type="button" aria-label={txt.close} onClick={onClose}><CloseIcon /></button></div>
        <div className="sub">
          <b>{book.book.title}</b>{total ? txt.pages(total) : ""}<br />
          {book.startedAt && from === 0 ? txt.started(dateShort(new Date(book.startedAt))) : txt.fromP(from)}
          {deadline ? txt.meeting(dateShort(deadline)) : txt.noMeetingDateYet}
          {paceToday ? <> · <span className="pace">{txt.aboutPagesADay(paceToday)}</span>{usualPace ? txt.yourUsualIs(usualPace) : "."}</> : null}
          {!deadline && txt.landmarksGetDatesOnce}
        </div>
        <form className="plan-list" onSubmit={(e) => { e.preventDefault(); if (valid) save.mutate(); }}>
          {total === null && (
            <label className="field"><span>{txt.howManyPagesIs}</span>
              <input id="plan-pages" className="input num" type="number" min={1} value={pages} onChange={(e) => setPages(e.target.value === "" ? "" : Number(e.target.value))} placeholder="275" /></label>
          )}
          {sorted.map((r, i) => {
            const d = r.date ? new Date(`${r.date}T12:00:00`) : r.page !== "" ? derivedDate(r.page) : null;
            const beyond = to !== "whole" && r.page !== "" && r.page > to;
            return (
              <div key={r.key} className={`lm${beyond ? " beyond" : ""}`}>
                <input id={`lm-name-${r.key}`} className="input" value={r.name} placeholder={txt.part(i + 1)} maxLength={60} aria-label={txt.landmarkName}
                  onChange={(e) => setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, name: e.target.value } : x)))} />
                <span className="pg">{txt.toP}<input id={`lm-page-${r.key}`} className="input num" type="number" min={from + 1} max={total ?? undefined} value={r.page} aria-label={txt.page}
                  onChange={(e) => setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, page: e.target.value === "" ? "" : Number(e.target.value) } : x)))} /></span>
                <div className="when">
                  <CalendarIcon width={14} height={14} />
                  {beyond ? <span>{txt.nextStretch}</span>
                    : editingDate === r.key ? (
                      <input className="input" type="date" value={r.date ?? (d ? d.toISOString().slice(0, 10) : "")} autoFocus aria-label={txt.date}
                        onChange={(e) => setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, date: e.target.value || null } : x)))} onBlur={() => setEditingDate(null)} />
                    ) : d ? <span>{txt.by}<b>{dateShort(d)}</b> · {r.date ? txt.setByHand : txt.fromYourPace}</span> : <span>{txt.noDateYet}</span>}
                  {!beyond && deadline && editingDate !== r.key && (
                    <button type="button" className="link" onClick={() => setEditingDate(r.key)}>{r.date ? txt.reset : txt.edit}</button>
                  )}
                  {r.date && editingDate !== r.key && <button type="button" className="link" onClick={() => setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, date: null } : x)))}>{txt.usePace}</button>}
                  <button type="button" className="x" aria-label={txt.removeLandmark} onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}><CloseIcon width={16} height={16} /></button>
                </div>
              </div>
            );
          })}
          {hint && rows.length > 0 && rows.at(-1)!.page === hint.page && (
            <div className="hint">{txt.halfwayBetweenAndIs(hint.lo === from && from === 0 ? txt.theStart : txt.p(hint.lo), hint.hi === total ? txt.theEnd : txt.p2(hint.hi))}<b>{txt.p3(hint.page)}</b>{txt.nearestChapterBreak}</div>
          )}
          {rows.length < 8 && total !== null && <button type="button" className="btn ghost add" onClick={addRow}><PlusIcon width={16} height={16} />{txt.addALandmark}</button>}

          <div className="covers">
            <div className="lbl">{txt.meetingCovers}</div>
            <label className="radio"><input type="radio" name="covers" checked={!other && to === "whole"} onChange={() => { setOther(false); setTo("whole"); }} /><span>{txt.theWholeBook}</span></label>
            {sorted.filter((r) => r.page !== "").map((r, i) => (
              <label key={r.key} className="radio"><input type="radio" name="covers" checked={!other && to === r.page} onChange={() => { setOther(false); setTo(r.page as number); }} />
                <span>{txt.through(r.name.trim() || txt.part(i + 1))}<small>{txt.p4(r.page, closest?.key === r.key ? txt.closestToYourUsual(usualPace) : "")}</small></span></label>
            ))}
            <label className="radio"><input type="radio" name="covers" checked={other || (to !== "whole" && !sorted.some((r) => r.page === to))}
              onChange={() => { setOther(true); if (custom !== "") setTo(custom); customRef.current?.focus(); }} />
              <span className="fixed">{txt.anotherPage}</span> <input ref={customRef} className="input num" type="number" min={from + 1} max={total ?? undefined} value={custom} placeholder="—" aria-label={txt.pageTheMeetingCovers}
                onFocus={() => setOther(true)}
                onChange={(e) => { const v = e.target.value === "" ? "" : Number(e.target.value); setCustom(v); setOther(true); if (v !== "") setTo(v); }} /></label>
            {reachAtUsual !== null && total !== null && reachAtUsual < total && !closest && <p className="muted small">{txt.atYourUsual}{usualPace}{txt.aDayYouD(reachAtUsual)}</p>}
          </div>
          {pace !== null && to !== "whole" && <p className="muted small">{txt.thatSAboutPages(pace)}</p>}
          {save.isError && <p className="error">{describeError(save.error)}</p>}
          <div className="foot">
            <button type="button" className="btn ghost" onClick={onClose}>{txt.cancel}</button>
            <button type="submit" className="btn primary" disabled={!valid || save.isPending}>{save.isPending ? txt.saving : txt.savePlan}</button>
          </div>
        </form>
      </div>
    </>
  , document.body);
}
